import gsap from "gsap"
import { useEffect, useRef, useState } from "react"

export const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max)
}
export const lerp = (min, max, value) => {
  return min + (max - min) * value
}
export const lerpClamped = (v0, v1, t) => {
  const lerpValue = v0 * (1 - t) + v1 * t
  return Math.min(Math.max(lerpValue, v0), v1)
}
export const inverseLerp = (value, min, max) => {
  return (value - min) / (max - min)
}
export const inverseLerpClamped = (value, min, max) => {
  const lerpValue = (value - min) / (max - min)
  return Math.min(Math.max(lerpValue, 0.0), 1.0)
}

export const easeOutCirc = (x) => Math.sqrt(1 - Math.pow(x - 1, 2))
export const easeInCirc = (x) => 1 - Math.sqrt(1 - Math.pow(x, 2))

export const easeInOutCubic = (x) => {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2
}
export const easeInOutQuint = (x) => {
  return x < 0.5 ? 16 * x * x * x * x * x : 1 - Math.pow(-2 * x + 2, 5) / 2
}

export const easeInQuad = (x) => x * x

export const easeInCubic = (x) => x * x * x
export const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3)

export const easeInQuart = (x) => x * x * x * x

export const easeOutQuint = (x) => 1 - Math.pow(1 - x, 5)

export const mapClamped = (value, inMin, inMax, outMin, outMax) => {
  let valueClamped = value
  if (valueClamped > inMax) {
    valueClamped = inMax
  } else if (valueClamped < inMin) {
    valueClamped = inMin
  }

  return ((valueClamped - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin
}

export const getRandom = (range) => lerp(-range, range, Math.random())

// https://stackoverflow.com/a/39077686
export const hexToRgb = (hex) =>
  hex
    .replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      (m, r, g, b) => "#" + r + r + g + g + b + b
    )
    .substring(1)
    .match(/.{2}/g)
    .map((x) => parseInt(x, 16))

export const addAlphaToHex = (color, alpha) => {
  if (color.startsWith("#")) {
    const rgb = hexToRgb(color)
    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
  }

  return color
}

export function usePixelScalingAndReset(svgRef, imageSize) {
  let currentPixelScale = useRef(0.8)
  const [pixelScaler, setPixelScaler] = useState(0.8)

  let onResize = useRef(() => {})

  useEffect(() => {
    onResize.current = () => {
      if (svgRef.current !== null) {
        /*
         * During resize the svg value for height takes a second to complete
         * Below I added setTimeout since height value was incorrect when ...
         * running before component has rerendered, this pushes method off
         * callstack until component is fully rerendered, then sets pixelScaler state
         */
        setTimeout(() => {
          let s = imageSize[1] / svgRef.current.getBoundingClientRect().height
          s = Math.min(0.95, s)

          if (currentPixelScale.current !== s) {
            setPixelScaler(s)
            currentPixelScale.current = s
          }
        })
      }
    }
    onResize.current()
    window.setTimeout(onResize.current, 400)
  }, [svgRef, imageSize])

  useEffect(() => {
    let resize = onResize.current
    window.addEventListener("resize", resize)
    return () => {
      window.removeEventListener("resize", resize)
    }
  }, [])

  return [pixelScaler]
}

export const onActiveListener = (active, render, wrapperRef, onChange) => {
  if (wrapperRef.current !== null) {
    window.setTimeout(() => {
      if (active && wrapperRef.current !== null) {
        wrapperRef.current.querySelectorAll("animate").forEach((element) => {
          element.beginElement()
        })
        wrapperRef.current
          .querySelectorAll("animateTransform")
          .forEach((element) => {
            element.beginElement()
          })
      }

      if (onChange !== undefined) {
        onChange(active, render)
      }

      gsap.to(wrapperRef.current, {
        autoAlpha: active ? 1.0 : 0.0,
        duration: 0.6,
        delay: 0.1,
      })
    }, 30)
  }
}

export const getValues = (numSteps, defaultVal, overrides, hold) => {
  let lastVal = overrides[0] || defaultVal
  const values = new Array(numSteps).fill(null).map((_, i) => {
    if (hold && overrides[i] !== undefined) lastVal = overrides[i]
    return hold ? lastVal : overrides[i] || defaultVal
  })

  return values.join("; ")
}
export const getKeytimes = (numSteps, offsets, times) => {
  if (times !== undefined) {
    let currentTime = 0.0
    const keys = Object.keys(times)

    const duration =
      keys.reduce((acc, key) => acc + times[key], 0) -
      times[keys[keys.length - 1]]

    let val = 0.0

    return keys
      .map((key, i) => {
        val = (currentTime + (offsets[i] || 0)) / duration
        currentTime += times[key]
        return val.toString()
      })
      .join("; ")
  }

  return new Array(numSteps)
    .fill(null)
    .map((_, i) =>
      (i / (numSteps - 1) + (offsets[i] || 0) * (1 / (numSteps - 1))).toString()
    )
    .join("; ")
}
